import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { useRouter } from "../../lib/router.js";
import { useSession } from "../../lib/session.js";
import { isVisible, type Answers, type WizardSchema } from "../../lib/wizard.js";
import { Logo, PageSpinner } from "../../components/ui.js";
import { QuestionField } from "./QuestionField.js";
import { ReviewStep, type FinalizeResult } from "./ReviewStep.js";

export function OnboardingWizard() {
  const { refresh } = useSession();
  const { navigate } = useRouter();
  const [schema, setSchema] = useState<WizardSchema | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [sectionIndex, setSectionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [review, setReview] = useState<FinalizeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<{ answers: Answers; sectionIndex: number }>({ answers: {}, sectionIndex: 0 });
  latest.current = { answers, sectionIndex };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [schemaRes, progressRes] = await Promise.all([
          api.get<{ schema: WizardSchema }>("/api/onboarding/schema"),
          api.get<{ progress: { answers: Answers; furthestStepIndex: number } }>(
            "/api/onboarding/progress"
          )
        ]);
        if (cancelled) return;
        setSchema(schemaRes.schema);
        setAnswers(progressRes.progress.answers ?? {});
        setSectionIndex(
          Math.min(progressRes.progress.furthestStepIndex ?? 0, schemaRes.schema.sections.length - 1)
        );
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load onboarding.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (stepId: string | null) => {
    try {
      await api.put("/api/onboarding/progress", {
        currentStep: stepId,
        furthestStepIndex: latest.current.sectionIndex,
        answers: latest.current.answers
      });
      setSavedAt(new Date().toISOString());
    } catch {
      /* autosave is best-effort; the next change retries */
    }
  }, []);

  // Debounced autosave whenever answers change.
  useEffect(() => {
    if (loading || !schema) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void save(schema.sections[sectionIndex]?.id ?? null);
    }, 800);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, sectionIndex, loading, schema, save]);

  if (loading) return <PageSpinner />;
  if (error && !schema)
    return (
      <div className="auth-layout">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  if (!schema) return null;

  if (review) {
    return (
      <ReviewStep
        result={review}
        onBack={() => setReview(null)}
        onComplete={async () => {
          await api.post("/api/onboarding/complete").catch(() => undefined);
          await refresh();
          navigate("/today", { replace: true });
        }}
      />
    );
  }

  const section = schema.sections[sectionIndex]!;
  const visibleQuestions = section.questions.filter((q) => isVisible(q, answers));
  const isLast = sectionIndex === schema.sections.length - 1;
  const progress = Math.round(((sectionIndex + 1) / schema.sections.length) * 100);

  function setAnswer(id: string, value: unknown) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  async function next() {
    await save(section.id);
    if (isLast) {
      setFinalizing(true);
      setError(null);
      try {
        const result = await api.post<FinalizeResult>("/api/onboarding/finalize");
        setReview(result);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not finish onboarding.");
      } finally {
        setFinalizing(false);
      }
    } else {
      setSectionIndex((i) => Math.min(i + 1, schema!.sections.length - 1));
    }
  }

  function back() {
    setSectionIndex((i) => Math.max(0, i - 1));
  }

  return (
    <div className="onboarding">
      <div className="onboarding-head">
        <div className="row-between">
          <div className="row" style={{ gap: "var(--space-2)" }}>
            <Logo size={28} />
            <span style={{ fontWeight: 700 }}>ClovaForm</span>
          </div>
          <span className="dim text-xs">
            {savedAt ? "Saved — you can leave and resume anytime" : "Autosaving"}
          </span>
        </div>
        <div className="progress-track mt-4">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="dim text-xs mt-2">
          Section {sectionIndex + 1} of {schema.sections.length}
        </div>
      </div>

      <div className="onboarding-body">
        <h1 className="mt-4">{section.title}</h1>
        {section.intro && <p className="muted mt-2">{section.intro}</p>}

        {error && <div className="alert alert-danger mt-4">{error}</div>}

        <div className="stack mt-6">
          {visibleQuestions.map((q) => (
            <div className="question" key={q.id}>
              <label className="question-label">{q.label}</label>
              {q.help && <p className="dim text-xs" style={{ marginTop: -4 }}>{q.help}</p>}
              <div className="mt-2">
                <QuestionField
                  question={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswer(q.id, v)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="onboarding-foot">
        <button className="btn" onClick={back} disabled={sectionIndex === 0 || finalizing}>
          Back
        </button>
        <button className="btn btn-primary grow" onClick={next} disabled={finalizing}>
          {finalizing ? "Building your profile…" : isLast ? "Review & finish" : "Continue"}
        </button>
      </div>
    </div>
  );
}
